import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { ButtonControl } from "@/Common/Components/Buttons/ButtonControl";
import ButtonGroup from "@/Common/Components/Buttons/ButtonGroup";
import { TextAreaResizable } from "@/Common/Components/Inputs/TextAreaResizable";
import { Popup } from "@/Common/Components/Modals/Popup";
import { Person } from "@/Common/Models/Person/Person";
import { relativeStore } from "../Stores/RelativeStore";

interface IRelativeDeactivatePopupProps {
    person: Person;
    onClose: () => void;
}

class RelativeDeactivatePopupState {
    public deactivationNote: string;
    public selectedDeactivationReasonLookupId: string;

    public constructor(state: RelativeDeactivatePopupState = null) {
        if (state != null) {
            this.deactivationNote = state.deactivationNote;
            this.selectedDeactivationReasonLookupId = state.selectedDeactivationReasonLookupId;
        }
        else {
            this.deactivationNote = "";
            this.selectedDeactivationReasonLookupId = "";
        }
    }
}

class RelativeDeactivatePopup extends BaseReactComponent<IRelativeDeactivatePopupProps, RelativeDeactivatePopupState> {
    public constructor(props: IRelativeDeactivatePopupProps) {
        super(props);
        const state = new RelativeDeactivatePopupState();
        state.deactivationNote = this.props.person.inactivationNote ?? "";
        state.selectedDeactivationReasonLookupId = this.props.person.reasonForInactivityLookupId ?? "";
        this.state = state;
    }

    public render(): JSX.Element {
        const canDeactivate: boolean = this.state.selectedDeactivationReasonLookupId.length > 0 || this.props.person.isInactive;
        const title = this.props.person.isInactive
            ? cacheStore.GetString("RelativeFile_ReActivateRelativeLabel")
            : cacheStore.GetString("RelativeFile_DeactivateRelativeLabel");

        return (
            <Popup
                Id="popupmenu"
                title={title}
                draggable
                manageOverlay
                cssClass="memo"
                onCloseDelegate={() => this.props.onClose()}
            >
                <div className="p-body">
                    <div className="row">
                        <div className="col-lg-12">
                            <div className="row">
                                {!this.props.person.isInactive && (
                                    <div className="col-sm-12">
                                        <strong className="form-label">
                                            {cacheStore.GetString("RelativeFile_DeactivateRelativeMessageLabel")}
                                        </strong>
                                    </div>
                                )}
                                {this.props.person.isInactive && (
                                    <div className="col-sm-12">
                                        <strong className="form-label">
                                            {cacheStore.GetString("ReActivationMessage")}
                                        </strong>
                                    </div>
                                )}
                            </div>
                            <div className="row">
                                <div className="col-sm-12">
                                    &nbsp; &nbsp; &nbsp;
                                </div>
                            </div>

                            {!this.props.person.isInactive && (
                                <div>
                                    <div className="row">
                                        <div className="col-sm-12">
                                            <label className="form-label">
                                                {cacheStore.GetString("ReasonLabel")}
                                            </label>
                                        </div>
                                    </div>

                                    <div className="row">
                                        <div className="col-sm-12">
                                            <select
                                                onChange={e => this.OnReasonChange(e)}
                                                className="form-control myle-control"
                                                value={this.state.selectedDeactivationReasonLookupId}
                                            >
                                                <option key="" value="" />
                                                {cacheStore.FindLookupsByTypeCode("INACTIVITYREASON").map(reason => (
                                                    <option key={reason.LookupID} value={reason.LookupID}>
                                                        {cacheStore.GetLocalizedValueByLookupId(reason.LookupID)}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                    <div className="row">
                                        <div className="col-sm-12">
                                            &nbsp; &nbsp; &nbsp;
                                        </div>
                                    </div>
                                    <div className="row">
                                        <div className="col-sm-12">
                                            <label className="form-label">{cacheStore.GetString("NotesLabel")}</label>
                                        </div>
                                    </div>
                                    <div className="row">
                                        <div className="col-sm-12">
                                            <TextAreaResizable
                                                id="deactivateNote"
                                                setCursorStartOnFocus={false}
                                                text={this.props.person.inactivationNote}
                                                isDisabled={false}
                                                onChange={value => this.DeactivationNoteChange(value)}
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}
                            {this.props.person.isInactive && (
                                <div className="row">
                                    <div className="col-sm-12">
                                        <span>
                                            {cacheStore.GetLocalizedValueByLookupId(this.props.person.reasonForInactivityLookupId)}
                                        </span>
                                        {this.props.person.inactivationNote && this.props.person.inactivationNote.length > 0 &&
                                            <span> ({this.props.person.inactivationNote})</span>
                                        }
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                <div className="p-footer flex-display flex-end">
                    <ButtonGroup>
                        <ButtonControl
                            variant="neutral"
                            onClick={() => this.props.onClose()}
                            label={cacheStore.GetString("CancelLabel")}
                        />
                        <ButtonControl
                            disabled={!canDeactivate}
                            variant="brand"
                            onClick={e => this.SaveClick(e)}
                            label={cacheStore.GetString("ContinueLabel")}
                        />
                    </ButtonGroup>
                </div>
            </Popup>
        );
    }

    private DeactivationNoteChange(value: string): void {
        const state = new RelativeDeactivatePopupState(this.state);
        state.deactivationNote = value;
        this.setState(state);
    }

    private OnReasonChange(e: React.ChangeEvent<HTMLSelectElement>): void {
        e.preventDefault();
        const state = new RelativeDeactivatePopupState(this.state);
        state.selectedDeactivationReasonLookupId = e.target.value;
        this.setState(state);
    }

    private SaveClick(e: React.MouseEvent): void {
        e.preventDefault();

        if (this.props.person && this.props.person.isInactive) {
            relativeStore.actions.reactivateRelative(this.props.person.ContactId);
        }
        else {
            relativeStore.actions.deactivateRelative(this.props.person.ContactId, this.state.selectedDeactivationReasonLookupId, this.state.deactivationNote);
        }
        this.props.onClose();
    }
}

export { RelativeDeactivatePopup };
